/**
 * AddWishModal — ajoute un souhait de commande (« À commander ») pour une cuvée précise.
 * Millésime + format + quantité + note, fournisseur déduit du domaine.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { ShoppingBag } from 'lucide-react';
import { achatsApi } from '../../services/api';
import { Button } from '../../components/ui/kit';
import { BOTTLE_FORMAT_OPTIONS } from '../../constants';

export default function AddWishModal({ preset, onClose }) {
  const qc = useQueryClient();
  const [year, setYear] = useState(preset.year || '');
  const [format, setFormat] = useState(preset.format || '750');
  const [quantity, setQuantity] = useState(6);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await achatsApi.saveWish({
        cuvee_id: preset.cuvee_id, cuvee_name: preset.cuvee_name, domain_id: preset.domain_id,
        domain_name: preset.domain_name, supplier_name: preset.domain_name, color: preset.color,
        year, format, quantity, note,
      });
      qc.invalidateQueries({ queryKey: ['achats-summary'] });
      qc.invalidateQueries({ queryKey: ['achats-wishes'] });
      toast.success('Ajouté aux souhaits de commande');
      onClose();
    } catch (e) { toast.error(e.message || 'Échec'); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 animate-fade-in" onClick={onClose}>
      <div className="bg-paper border border-hair rounded-lg shadow-xl p-5 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="add-wish-modal">
        <h3 className="font-title text-2xl mb-1 flex items-center gap-2"><ShoppingBag className="w-5 h-5 text-gold700" /> À commander</h3>
        <p className="text-[13px] text-n600 mb-4">{preset.cuvee_name}{preset.domain_name ? ` · ${preset.domain_name}` : ''}</p>
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className="text-[12px] text-n600">Millésime</span>
            <input className="vd-input h-9" value={year} onChange={(e) => setYear(e.target.value)} placeholder="ex. 2021" data-testid="wish-year" /></label>
          <label className="block"><span className="text-[12px] text-n600">Format</span>
            <select className="vd-input h-9" value={format} onChange={(e) => setFormat(e.target.value)} data-testid="wish-format">
              {BOTTLE_FORMAT_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select></label>
          <label className="block"><span className="text-[12px] text-n600">Quantité</span>
            <input type="number" min="1" className="vd-input h-9 nums" value={quantity} onChange={(e) => setQuantity(Number(e.target.value))} data-testid="wish-quantity" /></label>
        </div>
        <label className="block mt-3"><span className="text-[12px] text-n600">Note</span>
          <textarea rows={2} className="vd-input py-2 h-auto" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Précision (optionnel)" data-testid="wish-note" /></label>
        <div className="flex justify-end gap-2 mt-5">
          <Button onClick={onClose}>Annuler</Button>
          <Button variant="primary" onClick={save} disabled={busy} data-testid="wish-save">Ajouter</Button>
        </div>
      </div>
    </div>
  );
}
